import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import IntegrationApi, { RmmScriptCatalogItem } from "../apis/integrationApi";
import { triggerBlobDownload } from "../utils/downloadUtils";

type RmmScriptExportProvider = "datto" | "ninjaone";

interface DattoScriptExportDialogProps {
  open: boolean;
  onClose: () => void;
  provider?: RmmScriptExportProvider;
}

type DownloadTarget = "windows-shell" | "macos-shell" | "windows-scripts" | "macos-scripts";
type ScriptOs = "windows" | "macos";

const sanitizeSlug = (slug: string) => (
  slug.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "")
);

const scriptComponentFileName = (slug: string) => `${sanitizeSlug(slug) || "component"}.cpt`;
const ninjaOneScriptFileName = (slug: string) => `${sanitizeSlug(slug) || "script"}.ps1`;
const macosScriptFileName = (slug: string) => `${sanitizeSlug(slug) || "script"}.sh`;

const providerConfig = {
  datto: {
    title: "Export Datto Components",
    remoteShellTitle: "Secure Shell .cpt Files",
    windowsScriptsTitle: "Windows Script Components",
    windowsScriptsArchiveFilename: "theo-windows-script-components.zip",
    macosScriptsArchiveFilename: "theo-macos-script-components.zip",
    windowsShellFilename: "theo-secure-shell-windows.cpt",
    macosShellFilename: "theo-secure-shell-macos.cpt",
    downloadSelectedLabel: "Download Selected Components",
    importNote: "",
    itemFileName: scriptComponentFileName,
    getCatalog: (api: IntegrationApi) => api.getDattoWindowsScriptCatalog(),
    getMacosCatalog: (api: IntegrationApi) => api.getDattoMacosScriptCatalog(),
    downloadWindowsShell: (api: IntegrationApi) => api.downloadDattoWindowsShellSessionComponent(),
    downloadMacosShell: (api: IntegrationApi) => api.downloadDattoMacosShellSessionComponent(),
    downloadWindowsScripts: (api: IntegrationApi, slugs?: string[]) => api.downloadDattoWindowsScriptComponents(slugs),
    downloadMacosScripts: (api: IntegrationApi, slugs?: string[]) => api.downloadDattoMacosScriptComponents(slugs),
  },
  ninjaone: {
    title: "Export NinjaOne Scripts",
    remoteShellTitle: "Secure Shell Scripts",
    windowsScriptsTitle: "Windows Scripts",
    windowsScriptsArchiveFilename: "theo-windows-scripts.zip",
    macosScriptsArchiveFilename: "theo-macos-scripts.zip",
    windowsShellFilename: "theo-secure-shell-windows.ps1",
    macosShellFilename: "theo-secure-shell-macos.sh",
    downloadSelectedLabel: "Download Selected Scripts",
    importNote: "NinjaOne imports the script body from .ps1/.sh files. The downloaded scripts include a Theo metadata header with the recommended name, description, OS, run-as setting, and variables or parameters to set after import.",
    itemFileName: ninjaOneScriptFileName,
    getCatalog: (api: IntegrationApi) => api.getNinjaOneWindowsScriptCatalog(),
    getMacosCatalog: (api: IntegrationApi) => api.getNinjaOneMacosScriptCatalog(),
    downloadWindowsShell: (api: IntegrationApi) => api.downloadNinjaOneWindowsShellSessionScript(),
    downloadMacosShell: (api: IntegrationApi) => api.downloadNinjaOneMacosShellSessionScript(),
    downloadWindowsScripts: (api: IntegrationApi, slugs?: string[]) => api.downloadNinjaOneWindowsScripts(slugs),
    downloadMacosScripts: (api: IntegrationApi, slugs?: string[]) => api.downloadNinjaOneMacosScripts(slugs),
  },
};

const checkboxSx = {
  color: "#C7C7C7",
  "&.Mui-checked": {
    color: "#9B7AEF",
  },
};

const DattoScriptExportDialog: React.FC<DattoScriptExportDialogProps> = ({ open, onClose, provider = "datto" }) => {
  const [scripts, setScripts] = useState<RmmScriptCatalogItem[]>([]);
  const [scriptOs, setScriptOs] = useState<ScriptOs>("windows");
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [downloading, setDownloading] = useState<DownloadTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const config = providerConfig[provider];

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const loadCatalog = async () => {
      setLoadingCatalog(true);
      setError(null);
      setSearch("");
      try {
        const integrationApi = new IntegrationApi();
        const response = scriptOs === "macos"
          ? await config.getMacosCatalog(integrationApi)
          : await config.getCatalog(integrationApi);
        if (cancelled) return;
        const catalogScripts = Array.isArray(response?.scripts) ? response.scripts : [];
        setScripts(catalogScripts);
        setSelectedSlugs(catalogScripts.map((script) => script.slug));
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err.message || "Failed to load scripts.");
          setScripts([]);
          setSelectedSlugs([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingCatalog(false);
        }
      }
    };

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [config, open, scriptOs]);

  const selectedSlugSet = useMemo(() => new Set(selectedSlugs), [selectedSlugs]);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredScripts = useMemo(() => {
    if (!normalizedSearch) return scripts;
    return scripts.filter((script) => {
      const searchable = [
        script.title,
        script.slug,
        script.description,
        script.category,
      ].join(" ").toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [normalizedSearch, scripts]);

  const allFilteredSelected = filteredScripts.length > 0 && filteredScripts.every((script) => selectedSlugSet.has(script.slug));

  const toggleScript = (slug: string) => {
    setSelectedSlugs((prev) => (
      prev.includes(slug)
        ? prev.filter((selectedSlug) => selectedSlug !== slug)
        : [...prev, slug]
    ));
  };

  const toggleFilteredScripts = () => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredScripts.forEach((script) => next.delete(script.slug));
      } else {
        filteredScripts.forEach((script) => next.add(script.slug));
      }
      return Array.from(next);
    });
  };

  const downloadArtifact = async (target: DownloadTarget) => {
    setDownloading(target);
    setError(null);
    try {
      const integrationApi = new IntegrationApi();
      if (target === "windows-shell") {
        triggerBlobDownload(await config.downloadWindowsShell(integrationApi), config.windowsShellFilename);
      } else if (target === "macos-shell") {
        triggerBlobDownload(await config.downloadMacosShell(integrationApi), config.macosShellFilename);
      } else {
        const selectedScriptSlugs = selectedSlugs.length === scripts.length ? undefined : selectedSlugs;
        const isMacos = target === "macos-scripts";
        const filename = selectedScriptSlugs?.length === 1
          ? (isMacos && provider === "ninjaone" ? macosScriptFileName(selectedScriptSlugs[0]) : config.itemFileName(selectedScriptSlugs[0]))
          : (isMacos ? config.macosScriptsArchiveFilename : config.windowsScriptsArchiveFilename);
        const blob = isMacos
          ? await config.downloadMacosScripts(integrationApi, selectedScriptSlugs)
          : await config.downloadWindowsScripts(integrationApi, selectedScriptSlugs);
        triggerBlobDownload(blob, filename);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to download artifact.");
    } finally {
      setDownloading(null);
    }
  };

  const isBusy = loadingCatalog || Boolean(downloading);

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "8px", p: 1 } }}
    >
      <DialogTitle sx={{ fontSize: 26, fontWeight: 700, fontFamily: "var(--theo-font-sans)", color: "#19213D", pb: 1 }}>
        {config.title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isBusy}
          sx={{ position: "absolute", right: 16, top: 14, color: "#705D4A" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mx: 3 }} />
      <DialogContent sx={{ pt: 3 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {config.importNote ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {config.importNote}
          </Alert>
        ) : null}

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--theo-font-sans)", color: "#000", mb: 1.5 }}>
            {config.remoteShellTitle}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={downloading === "windows-shell" ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={() => downloadArtifact("windows-shell")}
              disabled={isBusy}
              sx={{ textTransform: "none", color: "#705D4A", borderColor: "#D9D1C7", fontWeight: 700 }}
            >
              Windows
            </Button>
            <Button
              variant="outlined"
              startIcon={downloading === "macos-shell" ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={() => downloadArtifact("macos-shell")}
              disabled={isBusy}
              sx={{ textTransform: "none", color: "#705D4A", borderColor: "#D9D1C7", fontWeight: 700 }}
            >
              macOS
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 3 }}>
          <Tabs
            value={scriptOs}
            onChange={(_event, value: ScriptOs) => setScriptOs(value)}
            sx={{ mb: 2, minHeight: 36 }}
          >
            <Tab value="windows" label="Windows actions" sx={{ textTransform: "none", minHeight: 36 }} />
            <Tab value="macos" label="macOS actions" sx={{ textTransform: "none", minHeight: 36 }} />
          </Tabs>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2, alignItems: "center" }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--theo-font-sans)", color: "#000" }}>
                {scriptOs === "macos" ? "macOS Scripts" : config.windowsScriptsTitle}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#666F8D", mt: 0.5, fontFamily: "var(--theo-font-sans)" }}>
                {`${selectedSlugs.length} selected`}
              </Typography>
            </Box>
            <Button
              size="small"
              sx={{ textTransform: "none", color: "#9B7AEF", fontWeight: 700, flexShrink: 0 }}
              onClick={toggleFilteredScripts}
              disabled={loadingCatalog || filteredScripts.length === 0}
            >
              {allFilteredSelected ? "Deselect Visible" : "Select Visible"}
            </Button>
          </Box>

          <TextField
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search scripts"
            size="small"
            InputProps={{ startAdornment: <SearchIcon sx={{ color: "#8A8FA3", mr: 1 }} /> }}
            sx={{
              mb: 2,
              "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)", fontSize: 14 },
            }}
          />

          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              maxHeight: 360,
              overflowY: "auto",
              backgroundColor: "#FFF",
            }}
          >
            {loadingCatalog ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredScripts.length === 0 ? (
              <Typography sx={{ color: "#666F8D", fontSize: 14, fontFamily: "var(--theo-font-sans)", p: 2 }}>
                No scripts found.
              </Typography>
            ) : (
              filteredScripts.map((script) => (
                <Box
                  key={script.slug}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "44px 1fr",
                    gap: 1,
                    alignItems: "start",
                    p: 1.5,
                    borderBottom: "1px solid #F0F2F5",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Checkbox
                    checked={selectedSlugSet.has(script.slug)}
                    onChange={() => toggleScript(script.slug)}
                    disabled={isBusy}
                    sx={checkboxSx}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: 14, color: "#111827" }}>
                      {script.title}
                    </Typography>
                    {script.description ? (
                      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#666F8D", mt: 0.5 }}>
                        {script.description}
                      </Typography>
                    ) : null}
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#8A8FA3", mt: 0.5 }}>
                      {script.slug}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "flex-end", gap: 1 }}>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={isBusy}
          sx={{
            background: "#705D4A",
            color: "#fff",
            borderRadius: 2,
            minWidth: 70,
            textTransform: "none",
            fontWeight: 700,
            "&:hover": { background: "#5e4d3c" },
          }}
        >
          Back
        </Button>
        <Button
          onClick={() => downloadArtifact(scriptOs === "macos" ? "macos-scripts" : "windows-scripts")}
          variant="contained"
          disabled={isBusy || selectedSlugs.length === 0}
          startIcon={downloading === `${scriptOs}-scripts` ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          sx={{
            background: "#F5F5F5",
            color: "#232B44",
            borderRadius: 2,
            minWidth: 190,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { background: "#F5F5F5" },
          }}
        >
          {downloading === `${scriptOs}-scripts` ? "Downloading..." : config.downloadSelectedLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DattoScriptExportDialog;
